import { logger } from "@mgdx-libs/logger";
import httpStatus from "http-status";

import { errorHandlerReport } from "../";
import { ERROR_MESSAGE_TRY_AGAIN_LATER } from "../constants/ERROR_MESSAGE_TRY_AGAIN_LATER";
import {
  ApiBadRequestError,
  ApiError,
  ApiForbiddenError,
  ApiInternalServerError,
  ApiNotFoundError,
  ApiTimeoutError,
  ApiUnauthorizedError,
} from "../errors";
import { toApiErrorDetailMessage, toStatusUrlMessage } from "../utils/responseToErrorMessage";
import { getApiErrorCodeEnumToString } from "./errorCode";

type ApiErrorDetailBody = {
  code?: number;
  message?: string;
  detail_code?: string;
};

type ParseErrorResponse = (errorOrResponse: Response) => Promise<{ message?: string; detail: string }>;

export const parseErrorResponse: ParseErrorResponse = async (response) => {
  try {
    const json: ApiErrorDetailBody = await response.json();
    const message = getApiErrorCodeEnumToString(json.detail_code);

    return {
      message,
      detail: toApiErrorDetailMessage(response, json),
    };
  } catch (e) {
    logger.error(e);
    return { detail: toStatusUrlMessage(response) };
  }
};

type CheckErrorResponse = (errorOrResponse: unknown) => Promise<string>;

export const checkErrorResponse: CheckErrorResponse = async (errorOrResponse) => {
  let responseMessage = `${ERROR_MESSAGE_TRY_AGAIN_LATER}`;

  try {
    if (errorOrResponse instanceof DOMException && errorOrResponse.name === "AbortError") {
      throw new ApiTimeoutError(errorOrResponse);
    } else if (errorOrResponse instanceof Response) {
      const { message, detail } = await parseErrorResponse(errorOrResponse);
      if (message) {
        responseMessage = message;
      }

      if (errorOrResponse.status === httpStatus.BAD_REQUEST) {
        throw new ApiBadRequestError(detail);
      }

      if (errorOrResponse.status === httpStatus.UNAUTHORIZED) {
        throw new ApiUnauthorizedError(detail);
      }

      if (errorOrResponse.status === httpStatus.FORBIDDEN) {
        throw new ApiForbiddenError(detail);
      }

      if (errorOrResponse.status === httpStatus.NOT_FOUND) {
        throw new ApiNotFoundError(detail);
      }

      if (errorOrResponse.status === httpStatus.INTERNAL_SERVER_ERROR) {
        throw new ApiInternalServerError(detail);
      }
      // other response
      throw new ApiError(detail);
    } else if (errorOrResponse instanceof Error) {
      await errorHandlerReport(errorOrResponse);
    }
  } catch (error) {
    if (typeof error === "string" || error instanceof Error) {
      errorHandlerReport(error);
    } else {
      errorHandlerReport(new Error("Unknown error occurred"));
    }
  }

  return responseMessage;
};
